import { useCallback, useEffect, useState } from "react";
import { FileText, Loader2, PenLine, Plus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SignaturePad from "@/components/SignaturePad";
import { useAuth } from "@/context/AuthContext";
import VerificationPanel from "@/components/child/VerificationPanel";
import api, { API_URL, formatApiError } from "@/lib/api";

const FORM_TYPES = [
  { key: "trainee", label: "Trainee feedback", desc: "Young person's own end-of-programme survey (ratings + comments)." },
  { key: "parent_4weekly", label: "Parent 4-week review", desc: "ITT quality assurance questionnaire completed with parents at week 4." },
  { key: "parent_8weekly", label: "Parent 8-week review", desc: "ITT quality assurance questionnaire completed with parents at week 8." },
  { key: "parent_final", label: "Parent end-of-programme feedback", desc: "Parent satisfaction survey at the end of the programme." },
  { key: "parent_signoff", label: "Parent completion sign-off", desc: "Parent signs off completion after the final report. Signature required." },
];

const TYPE_BADGES = {
  trainee: "bg-cyan-50 text-cyan-700 border-transparent",
  parent_4weekly: "bg-orange-50 text-orange-700 border-transparent",
  parent_8weekly: "bg-amber-50 text-amber-700 border-transparent",
  parent_final: "bg-emerald-50 text-emerald-700 border-transparent",
  parent_signoff: "bg-ukgreen-light text-ukgreen border-transparent",
};

const RATING_ITEMS = [
  ["professionalism", "Professionalism and training of the travel trainer"],
  ["dignity_respect", "Dignity and respect afforded to your child by the trainer"],
  ["timekeeping", "The travel trainer's timekeeping"],
  ["office_communication", "Office staff communication regarding changes to your service"],
  ["consistency", "Consistency of training (regular trainer, choice and agreement on tasks)"],
  ["sensitivity_needs", "Sensitivity to your child's personal, cultural or religious needs"],
  ["overall_impression", "Overall impression of the service provided"],
  ["involvement_in_plan", "Involvement allowed in your child's training plan"],
  ["complaints_response", "Response to complaints or comments you may have made"],
];

const RATING_OPTIONS = [["excellent", "Excellent"], ["good", "Good"], ["fair", "Fair"], ["poor", "Poor"]];

// ITT Quality Assurance Questionnaire (the paper "itt 8 week review" form) — 4-week & 8-week reviews
const ITT_SCALE = [
  ["progress_since_week4", "How would you rate the progress that your child has made from week 4?"],
  ["programme_helping", "Are you happy with how the training programme is helping your child?"],
  ["communication", "How would you rate the communication between the manager, staff and yourself?"],
  ["trainer_understands_needs", "Are you happy with how the trainer understands your child's needs throughout the training at this stage?"],
  ["involvement_updates", "Do you feel that you are involved in all stages of the training and updates given?"],
  ["quality_training_support", "How would you rate the quality of training and support provided?"],
  ["child_happy_progress", "Do you feel your child is happy with their progress to date?"],
];
const ITT_SCALE_OPTIONS = [
  ["5", "5 — Extremely satisfied"], ["4", "4 — Fairly satisfied"], ["3", "3 — Acceptable"],
  ["2", "2 — Not very satisfied"], ["1", "1 — Fairly unsatisfied"], ["0", "0 — Very unsatisfied"], ["na", "N/A"],
];
const ITT_YESNO = [
  ["anything_to_change", "Is there anything that you would like to change?"],
  ["monthly_reviews_happy", "Are you happy with the monthly progress reviews that are being held?"],
  ["staff_friendly_polite", "Are staff friendly and polite?"],
];
const ITT_TEXTS = [
  ["do_well", "What do you think we do well?"],
  ["dont_do_well", "What do you think we don't do well?"],
  ["confidentiality_maintained", "Do you feel that confidentiality is maintained?"],
  ["noticed_change", "Have you noticed any change in your child since starting ITT (more confident, more aware of their surroundings when out in the community)?"],
  ["concerns", "Do you have any concerns about the Independent Travel Training?"],
];

const TRAINEE_SCALES = [
  ["confidence_before", "Confidence travelling independently BEFORE training", 10],
  ["confidence_after", "Confidence travelling independently AFTER training", 10],
  ["trainer_clarity", "Was the information given by your trainer easy to understand?", 7],
  ["trainer_friendliness", "Was your trainer friendly and helpful?", 7],
  ["confidence_trip_alone", "Confidence making the trained trip by yourself", 7],
  ["materials_rating", "How would you rate the training materials?", 7],
  ["confidence_new_route", "Confidence planning your own trip to a different location", 7],
];

const TRAINEE_TEXTS = [
  ["feelings_before", "How did you feel about travelling independently before training?"],
  ["worries", "Was there anything about travelling independently you were worried about?"],
  ["pace_suited", "Did the pace of the training suit you?"],
  ["enjoyed", "Which parts of your training did you particularly enjoy?"],
  ["not_enjoyed", "Which parts of your training did you particularly not enjoy?"],
  ["feelings_after", "How do you feel about travelling independently now you have completed training?"],
  ["further_training", "Any part you want further training on, or are still worried about?"],
  ["improvement_suggestions", "Suggestions on how we could improve our service"],
  ["confidence_reason", "Why do you feel this way about making the trip alone?"],
  ["other_comments", "Any other comments about the Travel Training Programme"],
];

const TRIP_PURPOSES = [["school", "School"], ["appointments", "Appointments"], ["shopping", "Shopping"], ["social", "Social"], ["work", "Work"], ["other", "Other"]];

const EMPTY_RESP = { respondent_name: "", respondent_relationship: "", form_date: "" };

function ScaleRow({ field, label, max, value, onChange }) {
  return (
    <div className="space-y-1.5" data-testid={`fb-scale-${field}`}>
      <Label className="text-xs">{label} (1–{max})</Label>
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`fb-scale-${field}-${n}`}
            onClick={() => onChange(field, n)}
            className={`h-9 w-9 rounded-md border text-sm font-medium transition-colors duration-150 ${
              value === n ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-slate-50"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FeedbackTab({ child }) {
  const { hasPermission } = useAuth();
  const canManageFeedback = hasPermission("feedback:manage");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [meta, setMeta] = useState(EMPTY_RESP);
  const [responses, setResponses] = useState({});
  const [sigData, setSigData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/feedback?child_id=${child.id}`);
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => { load(); }, [load]);

  const openDialog = (type) => {
    setDialog(type);
    setMeta({ ...EMPTY_RESP, form_date: new Date().toISOString().slice(0, 10) });
    setResponses({});
    setSigData(null);
  };

  const setScale = (field, value) => setResponses((r) => ({ ...r, [field]: value }));
  const setRating = (key, field, value) =>
    setResponses((r) => ({ ...r, ratings: { ...(r.ratings || {}), [key]: { ...((r.ratings || {})[key] || {}), [field]: value } } }));
  const setItt = (bucket, key, field, value) =>
    setResponses((r) => ({ ...r, [bucket]: { ...(r[bucket] || {}), [key]: { ...((r[bucket] || {})[key] || {}), [field]: value } } }));
  const togglePurpose = (p) =>
    setResponses((r) => {
      const cur = r.trip_purposes || [];
      return { ...r, trip_purposes: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p] };
    });

  const save = async () => {
    setSaving(true);
    try {
      let signature_document_id = null;
      if (sigData) {
        const blob = await (await fetch(sigData)).blob();
        const fd = new FormData();
        fd.append("file", new File([blob], "feedback-signature.png", { type: "image/png" }));
        fd.append("category", "consent_document");
        fd.append("child_id", child.id);
        fd.append("description", `On-screen signature — ${meta.respondent_name || "respondent"} (${dialog})`);
        const { data: sigDoc } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        signature_document_id = sigDoc.id;
      }
      await api.post(`/feedback/children/${child.id}`, {
        form_type: dialog,
        respondent_name: meta.respondent_name || null,
        respondent_relationship: meta.respondent_relationship || null,
        form_date: meta.form_date || null,
        responses,
        signature_document_id,
      });
      toast.success("Feedback form recorded");
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const showSignature = ["trainee", "parent_4weekly", "parent_8weekly", "parent_final", "parent_signoff"].includes(dialog);
  const signoffInvalid = dialog === "parent_signoff" && (!meta.respondent_name.trim() || !sigData);

  return (
    <div className="space-y-4" data-testid="feedback-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Feedback &amp; sign-off</CardTitle>
          <p className="text-xs text-slate-500">
            Trainee and parent surveys plus the parent completion sign-off, digitised from the paper forms. Results feed quality monitoring.
          </p>
        </CardHeader>
        {canManageFeedback && (
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FORM_TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`feedback-new-${t.key}`}
                onClick={() => openDialog(t.key)}
                className="rounded-lg border border-border bg-white p-4 text-left hover:border-primary/50 hover:bg-slate-50 transition-colors duration-150 min-h-[64px]"
              >
                <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Plus className="h-4 w-4 text-primary" /> {t.label}
                </p>
                <p className="mt-1 text-xs text-slate-500">{t.desc}</p>
              </button>
            ))}
          </div>
        </CardContent>
        )}
      </Card>

      <VerificationPanel child={child} canManage={canManageFeedback} />

      <Card>
        <CardHeader><CardTitle className="text-base">Recorded forms</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-600" data-testid="feedback-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-600" data-testid="feedback-empty">No feedback recorded yet.</p>
          ) : (
            <div className="space-y-2" data-testid="feedback-list">
              {items.map((f) => (
                <div key={f.id} className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3" data-testid={`feedback-row-${f.id}`}>
                  <Badge className={TYPE_BADGES[f.form_type] || ""}>{FORM_TYPES.find((t) => t.key === f.form_type)?.label || f.form_type}</Badge>
                  <span className="font-mono text-xs text-slate-500">{f.form_number}</span>
                  <span className="text-xs text-slate-600">
                    {f.form_date || (f.created_at || "").slice(0, 10)}{f.respondent_name ? ` · ${f.respondent_name}` : ""} · by {f.completed_by_name}
                  </span>
                  {f.signature_document_id && (
                    <a href={`${API_URL}/files/${f.signature_document_id}/download`} target="_blank" rel="noreferrer"
                      data-testid={`feedback-signature-${f.id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      <PenLine className="h-3.5 w-3.5" /> Signature
                    </a>
                  )}
                  <a href={`${API_URL}/feedback/${f.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`feedback-pdf-${f.id}`}
                    className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <FileText className="h-3.5 w-3.5" /> PDF
                  </a>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle>{FORM_TYPES.find((t) => t.key === dialog)?.label}</DialogTitle>
            <DialogDescription>{FORM_TYPES.find((t) => t.key === dialog)?.desc}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{dialog === "trainee" ? "Trainee name" : "Parent/carer name"}{dialog === "parent_signoff" ? " *" : ""}</Label>
                <Input className="h-10" data-testid="fb-respondent-name" value={meta.respondent_name}
                  onChange={(e) => setMeta((m) => ({ ...m, respondent_name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Relationship</Label>
                <Input className="h-10" data-testid="fb-respondent-relationship" value={meta.respondent_relationship}
                  onChange={(e) => setMeta((m) => ({ ...m, respondent_relationship: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Form date</Label>
                <Input type="date" className="h-10" data-testid="fb-form-date" value={meta.form_date}
                  onChange={(e) => setMeta((m) => ({ ...m, form_date: e.target.value }))} />
              </div>
            </div>

            {(dialog === "parent_4weekly" || dialog === "parent_8weekly") && (
              <div className="space-y-3">
                {ITT_SCALE.map(([key, label]) => (
                  <div key={key} className="rounded-md border border-border p-3 space-y-2" data-testid={`fb-itt-${key}`}>
                    <p className="text-sm font-medium text-slate-800">{label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {ITT_SCALE_OPTIONS.map(([val, lab]) => (
                        <button key={val} type="button" data-testid={`fb-itt-${key}-${val}`}
                          onClick={() => setItt("scales", key, "score", val)}
                          className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors duration-150 min-h-[36px] ${
                            responses.scales?.[key]?.score === val ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-slate-50"
                          }`}>
                          {lab}
                        </button>
                      ))}
                    </div>
                    <Input className="h-9 text-xs" placeholder="Comments (optional)" value={responses.scales?.[key]?.comment || ""}
                      data-testid={`fb-itt-comment-${key}`} onChange={(e) => setItt("scales", key, "comment", e.target.value)} />
                  </div>
                ))}
                {ITT_YESNO.map(([key, label]) => (
                  <div key={key} className="rounded-md border border-border p-3 space-y-2" data-testid={`fb-itt-${key}`}>
                    <p className="text-sm font-medium text-slate-800">{label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[["yes", "Yes"], ["no", "No"]].map(([val, lab]) => (
                        <button key={val} type="button" data-testid={`fb-itt-${key}-${val}`}
                          onClick={() => setItt("yesno", key, "answer", val)}
                          className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors duration-150 min-h-[36px] ${
                            responses.yesno?.[key]?.answer === val ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-slate-50"
                          }`}>
                          {lab}
                        </button>
                      ))}
                    </div>
                    <Input className="h-9 text-xs" placeholder="Comments (optional)" value={responses.yesno?.[key]?.comment || ""}
                      data-testid={`fb-itt-comment-${key}`} onChange={(e) => setItt("yesno", key, "comment", e.target.value)} />
                  </div>
                ))}
                {ITT_TEXTS.map(([key, label]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-xs">{label}</Label>
                    <Textarea rows={2} data-testid={`fb-itt-text-${key}`} value={responses[key] || ""}
                      onChange={(e) => setResponses((r) => ({ ...r, [key]: e.target.value }))} />
                  </div>
                ))}
              </div>
            )}

            {dialog === "parent_final" && (
              <div className="space-y-3">
                {RATING_ITEMS.map(([key, label]) => (
                  <div key={key} className="rounded-md border border-border p-3 space-y-2" data-testid={`fb-rating-${key}`}>
                    <p className="text-sm font-medium text-slate-800">{label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {RATING_OPTIONS.map(([val, lab]) => (
                        <button key={val} type="button" data-testid={`fb-rating-${key}-${val}`}
                          onClick={() => setRating(key, "rating", val)}
                          className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors duration-150 min-h-[36px] ${
                            responses.ratings?.[key]?.rating === val ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-slate-50"
                          }`}>
                          {lab}
                        </button>
                      ))}
                    </div>
                    <Input className="h-9 text-xs" placeholder="Comment (optional)" value={responses.ratings?.[key]?.comment || ""}
                      data-testid={`fb-rating-comment-${key}`} onChange={(e) => setRating(key, "comment", e.target.value)} />
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label className="text-xs">Any other comments/feedback</Label>
                  <Textarea rows={3} data-testid="fb-comments" value={responses.comments || ""}
                    onChange={(e) => setResponses((r) => ({ ...r, comments: e.target.value }))} />
                </div>
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Overall satisfaction of our training</p>
                <div className="space-y-1.5">
                  <Label className="text-xs">What do we do well?</Label>
                  <Textarea rows={2} data-testid="fb-final-do-well" value={responses.do_well || ""}
                    onChange={(e) => setResponses((r) => ({ ...r, do_well: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">What could we do to improve?</Label>
                  <Textarea rows={2} data-testid="fb-final-could-improve" value={responses.could_improve || ""}
                    onChange={(e) => setResponses((r) => ({ ...r, could_improve: e.target.value }))} />
                </div>
              </div>
            )}

            {dialog === "trainee" && (
              <div className="space-y-4">
                {TRAINEE_SCALES.slice(0, 2).map(([f, l, m]) => (
                  <ScaleRow key={f} field={f} label={l} max={m} value={responses[f]} onChange={setScale} />
                ))}
                {TRAINEE_TEXTS.slice(0, 7).map(([f, l]) => (
                  <div key={f} className="space-y-1.5">
                    <Label className="text-xs">{l}</Label>
                    <Textarea rows={2} data-testid={`fb-text-${f}`} value={responses[f] || ""}
                      onChange={(e) => setResponses((r) => ({ ...r, [f]: e.target.value }))} />
                  </div>
                ))}
                {TRAINEE_SCALES.slice(2).map(([f, l, m]) => (
                  <ScaleRow key={f} field={f} label={l} max={m} value={responses[f]} onChange={setScale} />
                ))}
                <div className="space-y-1.5" data-testid="fb-made-trip">
                  <Label className="text-xs">Have you made the trip you were trained on by yourself?</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {[["yes", "Yes"], ["no_wont", "No, will not be making it"], ["no_later", "No, but will make it later"]].map(([val, lab]) => (
                      <button key={val} type="button" data-testid={`fb-made-trip-${val}`}
                        onClick={() => setResponses((r) => ({ ...r, made_trip: val }))}
                        className={`rounded-md border px-3 py-1.5 text-xs font-medium min-h-[36px] ${
                          responses.made_trip === val ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-slate-50"
                        }`}>
                        {lab}
                      </button>
                    ))}
                  </div>
                </div>
                {responses.made_trip === "yes" && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs">How many times since training?</Label>
                      <Input className="h-10 w-32" data-testid="fb-trip-count" value={responses.trip_count || ""}
                        onChange={(e) => setResponses((r) => ({ ...r, trip_count: e.target.value }))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">What did you use this trip for? (tick all that apply)</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {TRIP_PURPOSES.map(([val, lab]) => (
                          <button key={val} type="button" data-testid={`fb-purpose-${val}`} onClick={() => togglePurpose(val)}
                            className={`rounded-md border px-3 py-1.5 text-xs font-medium min-h-[36px] ${
                              (responses.trip_purposes || []).includes(val) ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-slate-50"
                            }`}>
                            {lab}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {(responses.made_trip === "no_wont" || responses.made_trip === "no_later") && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">If no, why haven't you made this trip?</Label>
                    <Textarea rows={2} data-testid="fb-text-why_no_trip" value={responses.why_no_trip || ""}
                      onChange={(e) => setResponses((r) => ({ ...r, why_no_trip: e.target.value }))} />
                  </div>
                )}
                <div className="space-y-1.5" data-testid="fb-would-recommend">
                  <Label className="text-xs">Would you recommend Travel Training to a friend or family member?</Label>
                  <div className="flex gap-1.5">
                    {[["yes", "Yes"], ["no", "No"]].map(([val, lab]) => (
                      <button key={val} type="button" data-testid={`fb-recommend-${val}`}
                        onClick={() => setResponses((r) => ({ ...r, would_recommend: val }))}
                        className={`rounded-md border px-4 py-1.5 text-xs font-medium min-h-[36px] ${
                          responses.would_recommend === val ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-slate-50"
                        }`}>
                        {lab}
                      </button>
                    ))}
                  </div>
                </div>
                {responses.would_recommend === "no" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">If no, why not?</Label>
                    <Textarea rows={2} data-testid="fb-text-why_not_recommend" value={responses.why_not_recommend || ""}
                      onChange={(e) => setResponses((r) => ({ ...r, why_not_recommend: e.target.value }))} />
                  </div>
                )}
                {TRAINEE_TEXTS.slice(7).map(([f, l]) => (
                  <div key={f} className="space-y-1.5">
                    <Label className="text-xs">{l}</Label>
                    <Textarea rows={2} data-testid={`fb-text-${f}`} value={responses[f] || ""}
                      onChange={(e) => setResponses((r) => ({ ...r, [f]: e.target.value }))} />
                  </div>
                ))}
              </div>
            )}

            {dialog === "parent_signoff" && (
              <div className="space-y-3">
                <p className="rounded-md bg-slate-50 border border-border p-3 text-sm text-slate-700" data-testid="fb-signoff-statement">
                  I confirm that I have received and discussed the final travel training report for my child,
                  and I sign off the completion of the Independent Travel Training programme.
                </p>
                <div className="space-y-1.5">
                  <Label className="text-xs">Comments (optional)</Label>
                  <Textarea rows={3} data-testid="fb-signoff-comments" value={responses.comments || ""}
                    onChange={(e) => setResponses((r) => ({ ...r, comments: e.target.value }))} />
                </div>
              </div>
            )}

            {showSignature && (
              <div className="space-y-1.5">
                <Label className="text-xs">
                  {dialog === "parent_signoff" ? "Parent/carer signature *"
                    : dialog === "trainee" ? "Trainee signature (optional)"
                    : "Parent/carer signature (optional)"}
                </Label>
                <SignaturePad onChange={setSigData} />
              </div>
            )}

            <Button className="w-full h-11" data-testid="feedback-save-button" disabled={saving || signoffInvalid} onClick={save}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record form"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
